import { db } from './firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';

// Contract status enum
export const CONTRACT_STATUS = {
  DRAFT: 'draft',
  SENT: 'sent',
  VIEWED: 'viewed',
  SIGNED: 'signed',
  DEPOSIT_PAID: 'deposit_paid',
  PAID: 'paid',
};

/**
 * Create a new contract from booking data
 */
export async function createContract(bookingId, bookingData) {
  if (!db) throw new Error('Firebase not initialized');

  const contractData = {
    bookingId,

    // Client info
    clientName: bookingData.contactName,
    clientEmail: bookingData.contactEmail,
    clientPhone: bookingData.contactPhone,
    organization: bookingData.organization || null,

    // Event details
    eventDate: bookingData.eventDate,
    eventTime: bookingData.eventTime,
    eventEndTime: null,
    eventType: bookingData.eventType,
    venue: bookingData.venue,
    venueAddress: bookingData.address
      ? `${bookingData.address}, ${bookingData.city || ''}, ${bookingData.state || ''}`
      : null,
    guestCount: bookingData.guestCount || null,

    // Artist & Package
    artistId: bookingData.artist,
    artistName: bookingData.artistName,
    packageId: bookingData.package,
    packageName: bookingData.packageName,
    packageDuration: bookingData.packageDuration,
    // Financial - left empty for admin to set manually in contract editor
    packagePrice: null,
    depositAmount: '',
    totalAmount: '',
    secondPaymentAmount: null,
    secondPaymentDue: null,

    // Customer notes from booking
    message: bookingData.message || null,

    // Sound & Backline
    requiresSound: 'no',
    soundBacklineCost: 0,

    // Status & Tracking
    status: CONTRACT_STATUS.DRAFT,
    signatureUrl: null,
    signedPdfUrl: null,
    stripePaymentLink: null, // For deposit payment
    stripeBalancePaymentLink: null, // For final/balance payment
    paymentId: null,
    balancePaymentId: null,

    // Timestamps
    createdAt: serverTimestamp(),
    sentAt: null,
    viewedAt: null,
    signedAt: null,
    depositPaidAt: null,
    paidAt: null,
  };

  const docRef = await addDoc(collection(db, 'contracts'), contractData);
  return { id: docRef.id, ...contractData };
}

/**
 * Update contract status
 */
export async function updateContractStatus(contractId, status, additionalData = {}) {
  if (!db) throw new Error('Firebase not initialized');

  const timestampField = {
    [CONTRACT_STATUS.SENT]: 'sentAt',
    [CONTRACT_STATUS.VIEWED]: 'viewedAt',
    [CONTRACT_STATUS.SIGNED]: 'signedAt',
    [CONTRACT_STATUS.DEPOSIT_PAID]: 'depositPaidAt',
    [CONTRACT_STATUS.PAID]: 'paidAt',
  }[status];

  const updateData = {
    status,
    ...additionalData,
  };

  if (timestampField) {
    updateData[timestampField] = serverTimestamp();
  }

  await updateDoc(doc(db, 'contracts', contractId), updateData);
}

/**
 * Get contract by ID
 */
export async function getContract(contractId) {
  if (!db) throw new Error('Firebase not initialized');

  const docSnap = await getDoc(doc(db, 'contracts', contractId));
  if (!docSnap.exists()) {
    throw new Error('Contract not found');
  }
  return { id: docSnap.id, ...docSnap.data() };
}

/**
 * Get contracts by status
 */
export async function getContractsByStatus(status) {
  if (!db) throw new Error('Firebase not initialized');

  const q = status
    ? query(
        collection(db, 'contracts'),
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      )
    : query(collection(db, 'contracts'), orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get all contracts with counts by status
 */
export async function getContractsWithStats() {
  if (!db) throw new Error('Firebase not initialized');

  const contracts = await getContractsByStatus(null);

  const stats = {
    total: contracts.length,
    draft: 0,
    sent: 0,
    viewed: 0,
    signed: 0,
    deposit_paid: 0,
    paid: 0,
  };

  contracts.forEach((contract) => {
    if (stats[contract.status] !== undefined) {
      stats[contract.status]++;
    }
  });

  return { contracts, stats };
}

/**
 * Mark contract as viewed (for tracking)
 */
export async function markContractViewed(contractId) {
  const contract = await getContract(contractId);

  // Only update if not already viewed or beyond
  if (contract.status === CONTRACT_STATUS.SENT) {
    await updateContractStatus(contractId, CONTRACT_STATUS.VIEWED);
  }

  return contract;
}

/**
 * Sign contract with signature URL
 */
export async function signContract(contractId, signatureUrl, signedPdfUrl = null) {
  await updateContractStatus(contractId, CONTRACT_STATUS.SIGNED, {
    signatureUrl,
    signedPdfUrl,
  });
}

/**
 * Get contract for booking
 */
export async function getContractByBookingId(bookingId) {
  if (!db) throw new Error('Firebase not initialized');

  const q = query(
    collection(db, 'contracts'),
    where('bookingId', '==', bookingId)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }

  return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
}

/**
 * Delete a contract
 */
export async function deleteContract(contractId) {
  if (!db) throw new Error('Firebase not initialized');
  await deleteDoc(doc(db, 'contracts', contractId));
}
